import React, { useState, useEffect } from 'react';
import { Row, Column, Header, Container, Input, Button } from "@front10/landing-page-book/dist/components";

const PDFDownload = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [downloadToken, setDownloadToken] = useState('');

  useEffect(() => {
    // Check if user came from Mailchimp success page
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const emailParam = urlParams.get('email');
    
    if (token && emailParam) {
      setDownloadToken(token);
      setEmail(emailParam);
      setSubmitted(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    // Generate a token for this email
    const token = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    
    // Store token in backend (via API call)
    try {
      const response = await fetch('/api/pdf/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, token })
      });
      
      if (response.ok) {
        // Set the token and email for download
        setDownloadToken(token);
        setSubmitted(true);
        
        // Submit to Mailchimp in background
        const form = document.getElementById('mc-embedded-subscribe-form');
        if (form) {
          const emailInput = form.querySelector('#mce-EMAIL');
          if (emailInput) {
            emailInput.value = email;
          }
          
          // Submit to Mailchimp (will open in new tab/window)
          form.submit();
        }
      } else {
        setError('Failed to generate download link. Please try again.');
      }
    } catch (err) {
      console.error('Error generating token:', err);
      setError('Something went wrong. Please try again.');
    }
  };

  const handleDownload = () => {
    if (downloadToken) {
      // Download PDF with token verification
      window.location.href = `/api/pdf/download?token=${downloadToken}&email=${encodeURIComponent(email)}`;
    }
  };

  return (
    <Container>
      <Row className="mt-5 mb-5">
        <Column className="col-md-8 offset-md-2 text-center">
          <Header type="h1" className="info-header" style={{marginBottom: '20px'}}>
            Get Your Free Decision-Making Framework
          </Header>
          <Header type="h4" className="info-header" style={{color: '#666', fontWeight: 'normal', marginBottom: '30px'}}>
            A practical guide to tracking and making better team decisions. Join our mailing list to download instantly.
          </Header>
        </Column>
      </Row>

      {!submitted ? (
        <Row>
          <Column className="col-md-6 offset-md-3">
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '40px',
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <form id="mc-embedded-subscribe-form" 
                    action="https://decisiontrackerbot.us14.list-manage.com/subscribe/post?u=16534b9fc8ab1eef168834584&amp;id=27c8660545&amp;f_id=0051ede0f0" 
                    method="post" 
                    name="mc-embedded-subscribe-form" 
                    className="validate" 
                    target="_blank"
                    onSubmit={handleSubmit}>
                <div id="mc_embed_signup_scroll">
                  <div style={{marginBottom: '20px'}}>
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      style={{width: '100%', padding: '12px', fontSize: '16px'}}
                    />
                  </div>
                  {error && (
                    <div style={{color: '#EF4F4F', marginBottom: '15px', fontSize: '14px'}}>
                      {error}
                    </div>
                  )}
                  <div style={{position: "absolute", left: "-5000px"}} aria-hidden="true">
                    <input type="text" name="b_16534b9fc8ab1eef168834584_27c8660545" tabIndex="-1" value=""/>
                  </div>
                  <Button 
                    type="submit" 
                    className="btn btn-primary btn-started"
                    style={{width: '100%', padding: '15px', fontSize: '18px'}}
                  >
                    Get Free PDF Download
                  </Button>
                </div>
              </form>
              
              <div style={{marginTop: '20px', fontSize: '14px', color: '#666', textAlign: 'center'}}>
                <p>✓ No spam, unsubscribe anytime</p>
                <p>✓ Instant download after signup</p>
                <p>✓ Free 7-day trial of Decision Tracker included</p>
              </div>
            </div>
          </Column>
        </Row>
      ) : (
        <Row>
          <Column className="col-md-6 offset-md-3">
            <div style={{
              backgroundColor: '#d4edda',
              padding: '40px',
              borderRadius: '8px',
              border: '2px solid #28a745',
              textAlign: 'center'
            }}>
              <div style={{fontSize: '48px', marginBottom: '20px'}}>✓</div>
              <Header type="h3" style={{marginBottom: '20px', color: '#155724'}}>
                Check Your Email!
              </Header>
              <p style={{fontSize: '16px', color: '#155724', marginBottom: '30px'}}>
                We've sent a download link to <strong>{email}</strong>. 
                Click the link in your email to download your free Decision-Making Framework PDF.
              </p>
              {downloadToken && (
                <div style={{marginTop: '30px'}}>
                  <Button 
                    onClick={handleDownload}
                    className="btn btn-primary btn-started"
                    style={{padding: '15px 30px', fontSize: '18px'}}
                  >
                    Download PDF Now
                  </Button>
                </div>
              )}
              <div style={{marginTop: '30px', fontSize: '14px', color: '#666'}}>
                <p>Didn't receive the email? Check your spam folder or <a href="/pdf-download" style={{color: '#EF4F4F'}}>try again</a>.</p>
              </div>
            </div>
          </Column>
        </Row>
      )}

      <Row className="mt-5 mb-5">
        <Column className="col-md-10 offset-md-1">
          <div style={{
            backgroundColor: '#fff',
            padding: '30px',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <Header type="h3" style={{marginBottom: '20px'}}>What's Inside the Framework?</Header>
            <Row>
              <Column className="col-md-6">
                <ul style={{lineHeight: '2'}}>
                  <li>7-step decision-making process</li>
                  <li>Best practices and common pitfalls</li>
                  <li>Decision type classifications</li>
                  <li>Stakeholder identification guide</li>
                </ul>
              </Column>
              <Column className="col-md-6">
                <ul style={{lineHeight: '2'}}>
                  <li>Context preservation strategies</li>
                  <li>Approval workflow templates</li>
                  <li>Real-world examples</li>
                  <li>Integration with Decision Tracker</li>
                </ul>
              </Column>
            </Row>
          </div>
        </Column>
      </Row>
    </Container>
  );
};

export default PDFDownload;




// WEBPACK FOOTER //
// ./src/components/PDFDownload.js